import { c as _c } from "react/compiler-runtime";
import type { ChatDetails, ChatMessage } from '@/features/chat/api/types';
import { getMessagePreviewText } from '@/features/chat/api/utils/get-message-preview-text';
import { CHAT_PAGE_SIZE } from '@/features/chat/constants';
import { useChatActions } from '@/features/chat/context/chat-actions-context';
import { generateMessageId, mergeStoredMessage, mergeStoredMessageAcrossPages } from '@/features/chat/utils';
import { forgetFailedSend, rememberFailedSend } from '@/features/chat/utils/failed-sends';
import { addMessageToOutbox, removeMessageFromOutbox, setSendInFlight } from '@/features/chat/utils/offline-outbox';
import { isRateLimitError, isTransportError } from '@/features/chat/utils/send-errors';
import { ChatStatus, SYSTEM_SENDER_ID } from '@/lib/chat-shared';
import { ChatType, MessageEventType, MessageType } from '@/lib/prisma/client';
import { toast } from '@/lib/toast';
import { trpc } from '@/trpc/client';
import type { AppRouter } from '@/trpc/routers/_app';
import type { InfiniteData } from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import type { UseTRPCMutationResult } from '@trpc/react-query/shared';
import type { inferProcedureInput, inferProcedureOutput } from '@trpc/server';
type UseMessageSendMutation = UseTRPCMutationResult<inferProcedureOutput<AppRouter['chat']['sendMessage']>, TRPCClientErrorLike<AppRouter>, inferProcedureInput<AppRouter['chat']['sendMessage']>, OptimisticUpdateResult>;
type InfiniteMessagesOutput = inferProcedureOutput<AppRouter['chat']['infiniteMessages']>;
type InfiniteMessagesData = InfiniteData<InfiniteMessagesOutput, string | null>;
interface OptimisticUpdateResult {
  previousChatData: ChatDetails | undefined;
  previousInfiniteData: InfiniteMessagesData | undefined;
  optimisticMessageId?: string;
}
const performOptimisticMessageUpdate = async (trpcUtils: ReturnType<typeof trpc.useUtils>, currentUser: string | undefined, {
  chatId,
  content,
  type,
  quotedMessageId,
  parentId,
  messageId
}: {
  chatId: string;
  content: string;
  type: MessageType;
  quotedMessageId?: string | undefined;
  parentId?: string | undefined;
  messageId?: string | undefined;
}): Promise<OptimisticUpdateResult> => {
  await trpcUtils.chat.chatDetails.cancel({
    chatId
  });
  await trpcUtils.chat.infiniteMessages.cancel({
    chatId,
    limit: CHAT_PAGE_SIZE,
    parentId: parentId ?? undefined
  });
  const previousChatData = trpcUtils.chat.chatDetails.getData({
    chatId
  });
  const previousInfiniteData = trpcUtils.chat.infiniteMessages.getInfiniteData({
    chatId,
    limit: CHAT_PAGE_SIZE,
    parentId: parentId ?? undefined
  }) as InfiniteMessagesData | undefined;

  // Find quoted message text if quotedMessageId is provided
  let quotedSnippet: string | undefined;
  if (typeof quotedMessageId === 'string' && quotedMessageId.length > 0) {
    // Search in infinite data and details cache
    const messagesFromInfinite = previousInfiniteData?.pages.flatMap(page => page.items) ?? [];
    const messagesFromDetails = previousChatData?.messages ?? [];
    const allCachedMessages = [...messagesFromInfinite, ...messagesFromDetails];
    const quotedMessage = allCachedMessages.find(m => m.id === quotedMessageId);
    if (quotedMessage) {
      const payload = quotedMessage.messagePayload;
      let text: string;
      if (typeof payload === 'string') {
        text = payload;
      } else {
        const textPayload = payload as Record<string, unknown>;
        text = typeof textPayload['text'] === 'string' ? textPayload['text'] : JSON.stringify(payload);
      }
      quotedSnippet = text.length > 100 ? `${text.slice(0, 100)}...` : text;
    }
  }

  // eslint-disable-next-line unicorn/prefer-global-this
  const isOffline = typeof window !== 'undefined' && !navigator.onLine;
  const optimisticMessage: ChatMessage = {
    // `messageId` is the id the server was asked to persist, so the optimistic bubble and
    // the stored row share it and no id swap is needed once the send resolves
    id: messageId ?? generateMessageId(),
    // the same payload shape the server stores, see `createMessage`
    messagePayload: type === MessageType.IMAGE_MSG ? {
      url: content
    } : {
      text: content.trim(),
      ...(typeof quotedMessageId === 'string' && quotedMessageId.length > 0 && {
        quotedMessageId,
        quotedSnippet
      })
    },
    createdAt: new Date(),
    senderId: currentUser,
    status: MessageEventType.CREATED,
    type,
    parentId: parentId ?? undefined,
    isPendingOffline: isOffline
  };

  // optimistically update the infinite messages
  trpcUtils.chat.infiniteMessages.setInfiniteData({
    chatId,
    limit: CHAT_PAGE_SIZE,
    parentId: parentId ?? undefined
  }, (data: InfiniteMessagesData | undefined): InfiniteMessagesData | undefined => {
    if (!data) {
      return {
        pages: [{
          items: [optimisticMessage],
          nextCursor: undefined
        }],
        // eslint-disable-next-line unicorn/no-null
        pageParams: [null]
      };
    }

    // a retry reuses the id of the failed bubble: turn that bubble back into a pending
    // one where it stands instead of adding a second copy
    const isRetry = data.pages.some(page => page.items.some(item => item.id === optimisticMessage.id));
    if (isRetry) {
      return {
        ...data,
        pages: data.pages.map(page => ({
          ...page,
          items: page.items.map(item => item.id === optimisticMessage.id ? {
            ...optimisticMessage,
            createdAt: item.createdAt
          } : item)
        }))
      };
    }
    return {
      ...data,
      pages: data.pages.map((page, index) => {
        if (index === 0) {
          return {
            ...page,
            items: [optimisticMessage, ...page.items]
          };
        }
        return page;
      })
    };
  });

  // optimistically update the chat details if this is not a thread reply
  if (!parentId) {
    trpcUtils.chat.chatDetails.setData({
      chatId
    }, (oldData: ChatDetails | undefined): ChatDetails => {
      if (!oldData) {
        return {
          // eslint-disable-next-line unicorn/no-null
          archivedAt: null,
          name: '',
          participants: [],
          id: chatId,
          messages: [optimisticMessage],
          capabilities: [],
          type: ChatType.ONE_TO_ONE,
          status: ChatStatus.OPEN
        };
      }
      if (oldData.messages.some(item => item.id === optimisticMessage.id)) {
        return {
          ...oldData,
          messages: oldData.messages.map(item => item.id === optimisticMessage.id ? {
            ...optimisticMessage,
            createdAt: item.createdAt
          } : item)
        };
      }
      return {
        ...oldData,
        messages: [...oldData.messages, optimisticMessage]
      };
    });
  }

  // optimistically update the chat overview
  trpcUtils.chat.chats.setData({}, oldChats => {
    if (!oldChats) return [];
    return oldChats.map(chat => {
      if (chat.id === chatId) {
        return {
          ...chat,
          lastMessage: {
            id: optimisticMessage.id,
            senderId: optimisticMessage.senderId ?? SYSTEM_SENDER_ID,
            // derived with the same helper the server uses, so the preview does not
            // change once the real message is synced back
            messagePreview: getMessagePreviewText({
              contentVersions: [{
                payload: optimisticMessage.messagePayload
              }]
            }),
            createdAt: optimisticMessage.createdAt,
            status: optimisticMessage.status,
            type: optimisticMessage.type
          },
          lastUpdate: optimisticMessage.createdAt,
          unreadCount: 0
        };
      }
      return chat;
    });
  });
  return {
    previousChatData,
    previousInfiniteData,
    optimisticMessageId: optimisticMessage.id
  };
};
export const useMessageSend = () => {
  const $ = _c(8);
  const trpcUtils = trpc.useUtils();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: currentUser
  } = trpc.chat.user.useQuery(t0);
  const {
    cancelQuote
  } = useChatActions();
  let t1;
  if ($[1] !== cancelQuote || $[2] !== currentUser || $[3] !== trpcUtils) {
    let t3;
    let t4;
    if ($[5] !== trpcUtils) {
      t3 = (error, variables, context) => {
        const {
          chatId: chatId_0,
          parentId: parentId_0
        } = variables;
        if (isTransportError(error) || isRateLimitError(error)) {
          if (context?.optimisticMessageId) {
            const optimisticMessageId = context.optimisticMessageId;
            const markPending = item => item.id === optimisticMessageId ? {
              ...item,
              isPendingOffline: true
            } : item;
            trpcUtils.chat.infiniteMessages.setInfiniteData({
              chatId: chatId_0,
              limit: CHAT_PAGE_SIZE,
              parentId: parentId_0 ?? undefined
            }, data => {
              if (!data) {
                return data;
              }
              return {
                ...data,
                pages: data.pages.map(page => ({
                  ...page,
                  items: page.items.map(item_0 => markPending(item_0))
                }))
              };
            });
            if (!parentId_0) {
              trpcUtils.chat.chatDetails.setData({
                chatId: chatId_0
              }, oldData => {
                if (!oldData) {
                  return oldData;
                }
                return {
                  ...oldData,
                  messages: oldData.messages.map(item_1 => markPending(item_1))
                };
              });
            }
          }
          if (context?.optimisticMessageId !== undefined) {
            forgetFailedSend(context.optimisticMessageId);
          }
          if (isTransportError(error)) {
            toast.success("Message queued. Will be sent when online.");
          }
          return;
        }
        console.error("Failed to send message, keeping it as a failed bubble:", error);
        const failedMessageId = context?.optimisticMessageId;
        if (failedMessageId === undefined) {
          return;
        }
        removeMessageFromOutbox(failedMessageId);
        markMessageSendFailed(trpcUtils, {
          chatId: chatId_0,
          parentId: parentId_0,
          messageId: failedMessageId
        });
        const failedMessage = trpcUtils.chat.infiniteMessages.getInfiniteData({
          chatId: chatId_0,
          limit: CHAT_PAGE_SIZE,
          parentId: parentId_0 ?? undefined
        })?.pages.flatMap(_temp).find(item_2 => item_2.id === failedMessageId);
        if (failedMessage) {
          rememberFailedSend(failedMessage, variables);
        }
        trpcUtils.chat.chats.invalidate();
      };
      t4 = (createdMessageData, t5, context_0) => {
        const {
          chatId: chatId_1,
          parentId: parentId_1
        } = t5;
        const createdMessage = createdMessageData as unknown as ChatMessage | undefined;
        if (createdMessage === undefined) {
          return;
        }
        const optimisticMessageId_0 = (context_0 as OptimisticUpdateResult | undefined)?.optimisticMessageId;
        if (optimisticMessageId_0 !== undefined) {
          removeMessageFromOutbox(optimisticMessageId_0);
          forgetFailedSend(optimisticMessageId_0);
        }
        trpcUtils.chat.infiniteMessages.setInfiniteData({
          chatId: chatId_1,
          limit: CHAT_PAGE_SIZE,
          parentId: parentId_1 ?? undefined
        }, data_0 => {
          if (!data_0) {
            return data_0;
          }
          const mergedPages = mergeStoredMessageAcrossPages(data_0.pages.map(_temp2), createdMessage, optimisticMessageId_0);
          return {
            ...data_0,
            pages: data_0.pages.map((page_2, index) => ({
              ...page_2,
              items: mergedPages[index] ?? page_2.items
            }))
          };
        });
        if (!parentId_1) {
          trpcUtils.chat.chatDetails.setData({
            chatId: chatId_1
          }, oldData_0 => {
            if (!oldData_0) {
              return oldData_0;
            }
            return {
              ...oldData_0,
              messages: mergeStoredMessage(oldData_0.messages, createdMessage, optimisticMessageId_0)
            };
          });
        }
        trpcUtils.chat.chats.invalidate();
      };
      $[5] = trpcUtils;
      $[6] = t3;
      $[7] = t4;
    } else {
      t3 = $[6];
      t4 = $[7];
    }
    t1 = {
      networkMode: "always",
      async onMutate(t2) {
        const {
          chatId,
          content,
          type,
          quotedMessageId,
          parentId,
          messageId,
          timestamp
        } = t2;
        const messageType = type ?? MessageType.TEXT_MSG;
        if (messageId !== undefined) {
          setSendInFlight(messageId, true);
          addMessageToOutbox({
            type: "MESSAGE",
            id: messageId,
            chatId,
            content,
            messageType,
            quotedMessageId: quotedMessageId ?? undefined,
            parentId: parentId ?? undefined,
            createdAt: (timestamp instanceof Date ? timestamp : new Date()).toISOString(),
            userId: currentUser
          });
        }
        cancelQuote();
        return performOptimisticMessageUpdate(trpcUtils, currentUser, {
          chatId,
          content,
          type: messageType,
          quotedMessageId: quotedMessageId ?? undefined,
          parentId: parentId ?? undefined,
          messageId: messageId ?? undefined
        });
      },
      onError: t3,
      onSuccess: t4,
      onSettled: _temp3
    };
    $[1] = cancelQuote;
    $[2] = currentUser;
    $[3] = trpcUtils;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return trpc.chat.sendMessage.useMutation(t1);
};

/**
 * Turns a message's bubble in the cached lists into a failed one, which shows an inline retry
 * instead of the queued or sending state.
 */
export const markMessageSendFailed = (trpcUtils: ReturnType<typeof trpc.useUtils>, {
  chatId,
  parentId,
  messageId
}: {
  chatId: string;
  parentId?: string | undefined;
  messageId: string;
}): void => {
  const markFailed = (item: ChatMessage): ChatMessage => item.id === messageId ? {
    ...item,
    sendFailed: true,
    isPendingOffline: false
  } : item;
  trpcUtils.chat.infiniteMessages.setInfiniteData({
    chatId,
    limit: CHAT_PAGE_SIZE,
    parentId: parentId ?? undefined
  }, (data: InfiniteMessagesData | undefined): InfiniteMessagesData | undefined => {
    if (!data) return data;
    return {
      ...data,
      pages: data.pages.map(page => ({
        ...page,
        items: page.items.map(item => markFailed(item))
      }))
    };
  });
  if (!parentId) {
    trpcUtils.chat.chatDetails.setData({
      chatId
    }, (oldData: ChatDetails | undefined): ChatDetails | undefined => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        messages: oldData.messages.map(item => markFailed(item))
      };
    });
  }
};
function _temp(page_0) {
  return page_0.items;
}
function _temp2(page_1) {
  return page_1.items;
}
function _temp3(_data, _error, t0) {
  const {
    messageId: messageId_0
  } = t0;
  if (messageId_0 !== undefined) {
    setSendInFlight(messageId_0, false);
  }
}